import { CurrentAdminRO } from '@routellers/types/dist/admin/current-admin.ro';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { api, setUnauthorizedHandler } from '../api';

type AuthContextValue = {
    admin: CurrentAdminRO | null;
    initializing: boolean;
    setAdmin: (admin: CurrentAdminRO | null) => void;
    refresh: () => Promise<void>;
    logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [admin, setAdmin] = useState<CurrentAdminRO | null>(null);
    const [initializing, setInitializing] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.admins.me(undefined);

            setAdmin(data);
        } catch {
            setAdmin(null);
        }
    }, []);

    useEffect(() => {
        setUnauthorizedHandler(() => setAdmin(null));
        refresh().finally(() => setInitializing(false));

        return () => setUnauthorizedHandler(null);
    }, [refresh]);

    const logout = useCallback(async () => {
        try {
            await api.admins.logout(undefined);
        } catch {
            // ignore — clear local session anyway
        } finally {
            setAdmin(null);
        }
    }, []);

    const value = useMemo(
        () => ({ admin, initializing, setAdmin, refresh, logout }),
        [admin, initializing, refresh, logout],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);

    if (!ctx) throw new Error('useAuth must be used within AuthProvider');

    return ctx;
};
